import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Music, X, Clock, Zap, Key, FolderOpen, Upload, HardDrive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrackLoader = ({ tracks = [], onSelect, onClose, targetDeck }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('crate'); // 'crate', 'all-crates', 'upload'
  const [allCrates, setAllCrates] = useState([]);
  const [selectedCrate, setSelectedCrate] = useState(null);
  const [cratesTracks, setCratesTracks] = useState([]);
  const [isLoadingCrates, setIsLoadingCrates] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Fetch all user crates
  useEffect(() => {
    if (activeTab === 'all-crates' && allCrates.length === 0) {
      fetchAllCrates();
    }
  }, [activeTab]);
  
  const fetchAllCrates = async () => {
    setIsLoadingCrates(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/crates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllCrates(data.crates || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch crates:', error);
    } finally {
      setIsLoadingCrates(false);
    }
  };
  
  // Get tracks to display based on active tab
  const getDisplayTracks = useCallback(() => {
    if (activeTab === 'crate') {
      return tracks;
    } else if (activeTab === 'all-crates' && selectedCrate) {
      return selectedCrate.tracks || [];
    }
    return [];
  }, [activeTab, tracks, selectedCrate]);
  
  // Filter tracks by search
  const filteredTracks = getDisplayTracks().filter(track => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      track.title?.toLowerCase().includes(query) ||
      track.name?.toLowerCase().includes(query) ||
      track.artist?.toLowerCase().includes(query) ||
      track.artists?.some(a => a.toLowerCase().includes(query))
    );
  });
  
  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '--:--';
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle local file upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      setUploadedFile(file);
    }
  };
  
  const handleLocalUpload = async () => {
    if (!uploadedFile) return;
    
    setIsUploading(true);
    try {
      // Create object URL for the local file
      const audioUrl = URL.createObjectURL(uploadedFile);
      
      // Extract filename without extension as title
      const fileName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      
      // Create a track object for the local file
      const localTrack = {
        id: `local-${Date.now()}`,
        title: fileName,
        artist: 'Local File',
        audio_url: audioUrl,
        duration_ms: 0, // Will be determined by wavesurfer
        isLocal: true
      };
      
      onSelect(localTrack);
    } catch (error) {
      console.error('Failed to load local file:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  const deckColor = targetDeck === 'A' ? '#00d4ff' : '#ff6b35';
  
  return (
    <div className="track-loader-overlay" onClick={onClose}>
      <div className="track-loader-modal" onClick={(e) => e.stopPropagation()}>
        <div className="track-loader-header">
          <div className="header-title">
            <Music size={20} style={{ color: deckColor }} />
            <span>Load Track to Deck <span style={{ color: deckColor }}>{targetDeck}</span></span>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="track-loader-tabs">
          <button 
            className={`tab-btn ${activeTab === 'crate' ? 'active' : ''}`}
            onClick={() => setActiveTab('crate')}
          >
            <FolderOpen size={14} />
            Current Crate
            {tracks.length > 0 && <span className="tab-count">{tracks.length}</span>}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'all-crates' ? 'active' : ''}`}
            onClick={() => setActiveTab('all-crates')}
          >
            <HardDrive size={14} />
            All Crates
          </button>
          <button 
            className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <Upload size={14} />
            Local File
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'upload' ? (
          <div className="upload-section">
            <input 
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            
            <div 
              className="upload-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={32} />
              <p>Click to select an audio file</p>
              <span className="upload-hint">Supports MP3, WAV, FLAC, AAC</span>
            </div>
            
            {uploadedFile && (
              <div className="uploaded-file-info">
                <Music size={16} />
                <span className="file-name">{uploadedFile.name}</span>
                <span className="file-size">
                  {(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB
                </span>
                <button 
                  className="load-file-btn"
                  onClick={handleLocalUpload}
                  disabled={isUploading}
                  style={{ borderColor: deckColor, color: deckColor }}
                >
                  {isUploading ? 'Loading...' : 'Load to Deck'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Crate Selector (for all-crates tab) */}
            {activeTab === 'all-crates' && (
              <div className="crate-selector">
                {isLoadingCrates ? (
                  <div className="loading-crates">Loading crates...</div>
                ) : (
                  <div className="crates-grid">
                    {allCrates.map((crate) => (
                      <button
                        key={crate._id || crate.id}
                        className={`crate-option ${selectedCrate?._id === crate._id ? 'selected' : ''}`}
                        onClick={() => setSelectedCrate(crate)}
                      >
                        <FolderOpen size={14} />
                        <span className="crate-name">{crate.name}</span>
                        <span className="crate-track-count">{crate.tracks?.length || 0}</span>
                      </button>
                    ))}
                    {allCrates.length === 0 && (
                      <div className="no-crates">No crates found</div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Search */}
            <div className="track-loader-search">
              <Search size={16} />
              <input 
                type="text"
                placeholder="Search tracks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            
            {/* Track List */}
            <div className="track-loader-list">
              {activeTab === 'all-crates' && !selectedCrate ? (
                <div className="no-tracks">
                  Select a crate above to browse tracks
                </div>
              ) : filteredTracks.length === 0 ? (
                <div className="no-tracks">
                  {searchQuery ? 'No tracks match your search' : 'No tracks available'}
                </div>
              ) : (
                filteredTracks.map((track, index) => (
                  <div 
                    key={track.id || index}
                    className="track-loader-item"
                    onClick={() => onSelect(track)}
                  >
                    {/* Album Art */}
                    <div className="track-art">
                      {track.album_art || track.artwork_url || track.image ? (
                        <img 
                          src={track.album_art || track.artwork_url || track.image} 
                          alt=""
                        />
                      ) : (
                        <Music size={20} />
                      )}
                    </div>
                    
                    {/* Track Info */}
                    <div className="track-info">
                      <div className="track-title">{track.title || track.name}</div>
                      <div className="track-artist">
                        {track.artist || track.artists?.join(', ') || 'Unknown Artist'}
                      </div>
                    </div>
                    
                    {/* Track Meta */}
                    <div className="track-meta">
                      {track.bpm && (
                        <span className="meta-bpm" title="BPM">
                          <Zap size={12} />
                          {track.bpm}
                        </span>
                      )}
                      {track.camelot && (
                        <span className="meta-key" title="Key">
                          <Key size={12} />
                          {track.camelot}
                        </span>
                      )}
                      <span className="meta-duration" title="Duration">
                        <Clock size={12} />
                        {formatDuration(track.duration_ms || track.duration)}
                      </span>
                    </div>
                    
                    {/* Audio Source Indicator */}
                    <div className="track-source">
                      {track.preview_url ? (
                        <span className="source-badge spotify">Spotify</span>
                      ) : track.soundcloud_stream_url ? (
                        <span className="source-badge soundcloud">SoundCloud</span>
                      ) : track.audio_url ? (
                        <span className="source-badge local">Local</span>
                      ) : (
                        <span className="source-badge no-audio">No Audio</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
        
        {/* Footer */}
        <div className="track-loader-footer">
          {activeTab !== 'upload' && (
            <span className="track-count">
              {filteredTracks.length} track{filteredTracks.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackLoader;
